import { useMemo } from "react"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { TrackingProperties } from "connected-components/analytics"
import {
  DEFAULT_KPI_STATE,
  KPI_PREFERENCE_SUBTYPE_BASE,
} from "connected-components/dashboard/store/kpi-slice"

/**
 * Tracked properties for the "KPI" analytics scope. Extends TrackingProperties
 * (`Record<string, unknown>`) — same pattern as AnalyticsContextProperties — so it carries the
 * index signature the provider's props sink requires while still naming the known fields.
 */
export interface KPIAnalyticsProperties extends TrackingProperties {
  selectedTab: string
  selectedGroup: string | undefined
}

/**
 * Panel-scoped KPI analytics properties, read directly from the RTK slice.
 *
 * Reads only the KPI slice slot — not the full useKPIData value — so it doesn't run
 * core/trade/model selectors or the seed effect just to pick a couple of fields. Dashboard
 * properties (modelId, instrumentId, selectedMode, etc.) are inherited through provider nesting
 * and must NOT be extracted here.
 *
 * @returns Memoized KPI analytics properties
 */
export function useKPIAnalyticsProperties(): KPIAnalyticsProperties {
  const dashboardId = useDashboardId()
  const panelId = usePanelId() ?? KPI_PREFERENCE_SUBTYPE_BASE
  const state = useAppSelector(
    (s) => s.dashboard.kpi[dashboardId]?.[panelId] ?? DEFAULT_KPI_STATE,
  )

  const { selectedTab, selectedGroup } = state
  const selectedGroupName = selectedGroup?.name
  const selectedGroupId = selectedGroup?.id

  // Memoize on primitives only so the tracked-properties object stays referentially stable.
  return useMemo<KPIAnalyticsProperties>(
    () => ({
      selectedTab,
      selectedGroup: selectedGroupName || selectedGroupId,
    }),
    [selectedTab, selectedGroupName, selectedGroupId],
  )
}
